import { useEffect, useState } from "react";
import {
  createRootRouteWithContext,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { Menu, X } from "lucide-react";
import { useAuth, roleAtLeast, type StoreRole } from "@/lib/auth";
import { requiredRoleFor } from "@/lib/routeRoles";
import { useApp } from "@/context/AppContext";
import { LoginScreen } from "@/components/LoginScreen";
import { InventoryMigrationBanner } from "@/components/InventoryMigrationBanner";
import { fetchSellerReservations } from "@/features/reservations";
import { useTheme } from "@/features/useTheme";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    component: RootLayout,
  },
);

// `min` gates a nav item by role (TNK-81). Items without `min` are visible to
// all roles. Staff (no min met for manager items) lose Products/Live/Sales/
// Reservations; Owner/Manager see everything.
const NAV_ITEMS: { to: string; label: string; min?: StoreRole }[] = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/lots", label: "Lots" },
  { to: "/inventory", label: "Inventory" },
  { to: "/products", label: "Products", min: "manager" },
  { to: "/live", label: "Live", min: "manager" },
  { to: "/sales", label: "Sales", min: "manager" },
  { to: "/reservations", label: "Reservations", min: "manager" },
  { to: "/tools", label: "Tools" },
  { to: "/settings", label: "Settings" },
];

/**
 * Shown in place of the page when a member's role is below what the route needs.
 * Says WHICH role is required and keeps the nav, so it reads as "not for you"
 * rather than a broken page — the failure mode this replaces was a shell that
 * 403'd request by request.
 */
function NotAuthorized({ needed }: { needed: StoreRole }) {
  return (
    <div className="mx-auto max-w-md py-16 text-center">
      <h1 className="text-xl font-semibold text-foreground">
        You don't have access to this page
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        It needs the <span className="font-medium capitalize">{needed}</span>{" "}
        role. Ask an owner if you need it.
      </p>
      <Link
        to="/dashboard"
        className="mt-6 inline-block rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        Back to dashboard
      </Link>
    </div>
  );
}

function RootLayout() {
  const auth = useAuth();
  const { appPreferences } = useApp();
  // TNK-154 — applies `<html data-theme="light|dark">` based on the
  // operator's pick; subscribes to OS theme changes when in System mode.
  useTheme(appPreferences.theme);
  const { location } = useRouterState();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingReservations, setPendingReservations] = useState(0);

  // Hide nav sections a role can't use (TNK-81). Server guards remain the
  // source of truth; this is UX. In dev-guest mode role is "owner" (full nav).
  const navItems = NAV_ITEMS.filter(
    (item) => !item.min || roleAtLeast(auth.role, item.min),
  );

  // Auto-close the drawer when route changes (covers Link clicks).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Pending-reservation badge. Refetched on navigation so it clears after the
  // seller acts on the queue. Guests / unauthed skip it (endpoint 401s/404s).
  useEffect(() => {
    if (
      !auth.isAuthenticated ||
      auth.isGuest ||
      !roleAtLeast(auth.role, "manager")
    ) {
      setPendingReservations(0);
      return;
    }
    let cancelled = false;
    void fetchSellerReservations()
      .then((rows) => {
        if (!cancelled) {
          setPendingReservations(
            rows.filter((r) => r.status === "Pending").length,
          );
        }
      })
      .catch(() => {
        /* badge is best-effort */
      });
    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, auth.isGuest, auth.role, location.pathname]);

  // Public storefront (TNK-51): /shop/* is customer-facing — render it outside
  // the seller auth gate and chrome. The /shop layout provides its own header.
  if (location.pathname === "/shop" || location.pathname.startsWith("/shop/")) {
    return <Outlet />;
  }

  // Invite acceptance (TNK-80): /join/:token is reached by a not-yet-signed-in
  // invitee, so it renders outside the auth gate. On success it sets the
  // session cookie and full-page-navigates into the app.
  if (location.pathname.startsWith("/join/")) {
    return <Outlet />;
  }

  // TNK-177 — root is a public→store / operator→dashboard redirector
  // (routes/index.tsx). Render it outside the gate so a signed-out visitor
  // lands on the storefront instead of the operator login.
  if (location.pathname === "/") {
    return <Outlet />;
  }

  if (auth.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  // TNK-177 — operators no longer log in from arbitrary paths. When signed out,
  // only the store's configured (secret, server-validated) door slug reveals the
  // LoginScreen; every other path is sent to the storefront so the operator app
  // stays hidden.
  if (!auth.isAuthenticated) {
    return <DoorResolver pathname={location.pathname} />;
  }

  // TCGMA-177 — is this path above the member's role? Plain computation, no
  // hooks, so it's safe below the early returns above.
  const needed = requiredRoleFor(location.pathname);
  const deniedRole = needed && !roleAtLeast(auth.role, needed) ? needed : null;

  return (
    <div className="flex h-full flex-col overflow-x-hidden">
      <header className="border-b border-border bg-card">
        <nav className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 text-sm md:px-6">
          {/* Logo + desktop nav */}
          <div className="flex items-center gap-6 min-w-0">
            <Link
              to="/dashboard"
              className="font-semibold text-foreground hover:text-primary"
            >
              TCG Manager
            </Link>
            <div className="hidden items-center gap-6 md:flex">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} current={location.pathname}>
                  {item.label}
                  {item.to === "/reservations" && pendingReservations > 0 && (
                    <span className="ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                      {pendingReservations}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Right cluster: guest badge + sign out (desktop) + hamburger (mobile) */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {import.meta.env.DEV && auth.isGuest && (
              <span className="rounded bg-muted px-2 py-1">guest</span>
            )}
            <button
              type="button"
              onClick={() => auth.logout()}
              className="hidden rounded border border-border px-3 py-1 hover:bg-muted md:inline-flex"
            >
              Sign out
            </button>
            <button
              type="button"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-foreground hover:bg-muted md:hidden"
            >
              {mobileOpen ? (
                <X className="h-4 w-4" />
              ) : (
                <Menu className="h-4 w-4" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="border-t border-border bg-card md:hidden">
            <div className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-2 text-sm">
              {navItems.map((item) => {
                const active =
                  location.pathname === item.to ||
                  (item.to !== "/" && location.pathname.startsWith(item.to));
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center gap-2 rounded-md px-3 py-2 ${
                      active
                        ? "bg-muted text-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    {item.label}
                    {item.to === "/reservations" && pendingReservations > 0 && (
                      <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                        {pendingReservations}
                      </span>
                    )}
                  </Link>
                );
              })}
              <button
                type="button"
                onClick={() => auth.logout()}
                className="mt-1 rounded-md border border-border px-3 py-2 text-left text-foreground hover:bg-muted"
              >
                Sign out
              </button>
            </div>
          </div>
        )}
      </header>
      <InventoryMigrationBanner />
      <main className="mx-auto w-full min-w-0 max-w-6xl flex-1 overflow-x-hidden px-4 py-6 md:px-6">
        {/* TCGMA-177 — role gate. RequireRole only wraps in-page controls, so
            without this a Staff member could deep-link into a Manager/Owner page
            and get a shell that 403s request by request. The server is still the
            real boundary; this just stops us rendering a page they can't use.
            Rendered INSIDE the chrome so they keep the nav and aren't stranded. */}
        {deniedRole ? <NotAuthorized needed={deniedRole} /> : <Outlet />}
      </main>
    </div>
  );
}

// TNK-177 — signed-out gate. The operator login lives only behind the store's
// configured door slug, validated server-side (the slug is never shipped to the
// client). The first path segment is checked against POST /api/store/door-check:
// a match reveals the LoginScreen (URL unchanged, so the existing return-path
// logic lands the operator on /dashboard via routes/$door.tsx after sign-in);
// anything else — a wrong slug, a guessed operator path, or an error — falls
// through to the storefront so the app stays invisible.
function DoorResolver({ pathname }: { pathname: string }) {
  const navigate = useNavigate();
  const [isDoor, setIsDoor] = useState(false);

  useEffect(() => {
    const slug = pathname.split("/").filter(Boolean)[0] ?? "";
    let cancelled = false;
    void (async () => {
      let match = false;
      try {
        const res = await fetch("/api/store/door-check", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ slug }),
        });
        match =
          res.ok &&
          (((await res.json()) as { match?: boolean }).match ?? false);
      } catch {
        match = false; // fail closed → storefront
      }
      if (cancelled) return;
      if (match) setIsDoor(true);
      else void navigate({ to: "/shop", replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname, navigate]);

  if (isDoor) return <LoginScreen />;
  return (
    <div className="flex h-full items-center justify-center text-muted-foreground">
      Loading…
    </div>
  );
}

function NavLink({
  to,
  current,
  children,
}: {
  to: string;
  current: string;
  children: React.ReactNode;
}) {
  const active = current === to || (to !== "/" && current.startsWith(to));
  return (
    <Link
      to={to}
      className={
        active
          ? "text-foreground"
          : "text-muted-foreground hover:text-foreground"
      }
    >
      {children}
    </Link>
  );
}
